import { useCallback, useEffect, useMemo, useState } from "react";
import useAuth from "./useAuth";
import {
  fetchVirtualNumbers,
  getCachedVirtualNumbers,
  subscribeToVirtualNumbers,
  updateVirtualNumber as updateVirtualNumberRequest,
  buildTagsByNumber
} from "../services/virtualNumbers";
import type {
  UpdateVirtualNumberRequest,
  VirtualNumberSettings
} from "../services/virtualNumbers";

/**
 * The authenticated user's manageable virtual numbers (their own, plus their
 * team's when they administer the org), with the customer-editable settings.
 *
 * Backed by the module-level cache in `services/virtualNumbers`, so the calls
 * page and the settings page share one fetch and both see a tag rename the
 * instant either of them saves it.
 */
export default function useVirtualNumbers() {
  const { user } = useAuth();
  const [virtualNumbers, setVirtualNumbers] = useState<VirtualNumberSettings[]>(
    () => getCachedVirtualNumbers() ?? []
  );
  const [loading, setLoading] = useState(() => getCachedVirtualNumbers() === null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => subscribeToVirtualNumbers(setVirtualNumbers), []);

  const refetch = useCallback(
    async (force = true) => {
      if (!user?.email) {
        setVirtualNumbers([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        await fetchVirtualNumbers(force);
      } catch (err) {
        console.error("useVirtualNumbers: failed to fetch virtual numbers", err);
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    },
    [user?.email]
  );

  useEffect(() => {
    // A warm cache renders instantly (the spinner is gated on having no rows),
    // and we still revalidate so a tag edited in another tab lands here.
    refetch(true);
  }, [refetch]);

  const update = useCallback(
    async (number: string, patch: UpdateVirtualNumberRequest): Promise<VirtualNumberSettings> =>
      updateVirtualNumberRequest(number, patch),
    []
  );

  const tagsByNumber = useMemo(() => buildTagsByNumber(virtualNumbers), [virtualNumbers]);

  return { virtualNumbers, tagsByNumber, loading, error, refetch, update };
}
